export type EvalModality = 'language' | 'coding' | 'image' | 'video' | 'open-models';

export interface EvalLaunch {
  title: string;
  model: string;
  description: string;
  href: string;
  surface: 'Gateway' | 'Comfy' | 'Agent' | 'Evals';
  tags: string[];
}

export interface EvalSource {
  slug: string;
  name: string;
  operator: string;
  eyebrow: string;
  description: string;
  measures: string[];
  usefulFor: string;
  caution: string;
  updateNote: string;
  href: string;
  methodologyHref: string;
  modalities: EvalModality[];
  launches: EvalLaunch[];
}

const compare = (models: string[]) => `/tools/model-compare?models=${encodeURIComponent(models.join(','))}`;

export const evalSources: EvalSource[] = [
  {
    slug: 'artificial-analysis',
    name: 'Artificial Analysis',
    operator: 'Artificial Analysis',
    eyebrow: 'Quality, price, and measured speed',
    description:
      'Independent model analysis spanning language intelligence, price and latency plus blind preference arenas for image and video generation.',
    measures: [
      'Language intelligence across agentic, coding, scientific-reasoning, and general tasks',
      'Provider price, output speed, latency, and context-window comparisons',
      'Blind human preference for text-to-image, image editing, and video modalities',
    ],
    usefulFor: 'Build a broad shortlist with quality, speed, and API price visible together.',
    caution:
      'Its Intelligence Index is a composite, and the media arenas measure relative preference at standardized defaults. Neither substitutes for your prompts, hardware, or failure modes.',
    updateNote: 'Live leaderboards; read current values at the source, not a copied snapshot.',
    href: 'https://artificialanalysis.ai/',
    methodologyHref: 'https://artificialanalysis.ai/methodology/intelligence-benchmarking',
    modalities: ['language', 'coding', 'image', 'video'],
    launches: [
      {
        title: 'Compare a frontier language panel',
        model: 'GPT · Claude · Gemini · DeepSeek',
        description: 'Send one production prompt to four routed models and inspect output, latency, and billed cost side by side.',
        href: compare(['gpt-5.5', 'claude-sonnet-4-6', 'gemini-3.8-flash', 'deepseek-v4-flash']),
        surface: 'Evals',
        tags: ['language', 'cost', 'latency'],
      },
      {
        title: 'Run GPT Image 2',
        model: 'gpt-image-2',
        description: 'Use the hosted image playground and OpenAI-compatible image endpoint for a prompt-level check.',
        href: '/spaces/gpt-image-2',
        surface: 'Gateway',
        tags: ['text-to-image', 'hosted'],
      },
      {
        title: 'Run open FLUX or SDXL',
        model: 'FLUX.1 · RealVisXL / SDXL',
        description: 'Try hosted FLUX, or open a deployable RealVisXL Comfy workflow with mirrored weights.',
        href: '/comfy?workflow=realvisxl-photo',
        surface: 'Comfy',
        tags: ['text-to-image', 'open-weights'],
      },
      {
        title: 'Run open video workflows',
        model: 'Wan VACE · LTX-Video',
        description: 'Move from the video leaderboard to tested image-to-video, text-to-video, or video-to-video workflow JSON.',
        href: '/comfy?workflow=wan-vace-video-to-video',
        surface: 'Comfy',
        tags: ['video-to-video', 'GPU'],
      },
    ],
  },
  {
    slug: 'arena',
    name: 'Arena',
    operator: 'LMArena / Arena',
    eyebrow: 'Blind human preference',
    description:
      'Anonymous pairwise human votes compare model responses and media outputs from the same prompt.',
    measures: [
      'Which response a voter prefers in a blind head-to-head',
      'Category and modality leaderboards derived from many pairwise votes',
      'Confidence ranges where the leaderboard exposes them',
    ],
    usefulFor: 'Find models people tend to prefer for open-ended chat, style, instruction following, and media quality.',
    caution:
      'Preference is audience- and prompt-distribution dependent. Elo-like ratings are relative, close ranks can overlap, and popularity is not correctness or safety.',
    updateNote: 'Continuously updated from new votes; use the live leaderboard and its current category filters.',
    href: 'https://arena.ai/leaderboard',
    methodologyHref: 'https://lmarena.ai/',
    modalities: ['language', 'image', 'video'],
    launches: [
      {
        title: 'Recreate a blind-text shortlist',
        model: 'app/auto · frontier panel',
        description: 'Start with a shared prompt, compare exact outputs, then repeat with your own acceptance criteria.',
        href: compare(['app/auto', 'gpt-5.5', 'claude-sonnet-4-6', 'gemini-3.8-flash']),
        surface: 'Evals',
        tags: ['human preference', 'language'],
      },
      {
        title: 'Compare image families',
        model: 'GPT Image · FLUX · SDXL',
        description: 'Run proprietary hosted models beside open workflows rather than treating one global preference score as universal.',
        href: '/spaces',
        surface: 'Gateway',
        tags: ['image', 'playgrounds'],
      },
    ],
  },
  {
    slug: 'livebench',
    name: 'LiveBench',
    operator: 'LiveBench',
    eyebrow: 'Fresh, objective language tasks',
    description:
      'A frequently refreshed benchmark: recent questions, objective ground-truth scoring, reduced test contamination.',
    measures: [
      'Math, coding, reasoning, language, instruction following, and data analysis',
      'Objective answers rather than a model judge where possible',
      'Performance on questions sourced or transformed from recent material',
    ],
    usefulFor: 'Checking whether a language-model shortlist still generalizes beyond old, heavily circulated benchmark sets.',
    caution:
      'Freshness reduces one contamination risk; it does not make the category mix identical to your users or account for end-to-end agent scaffolding.',
    updateNote: 'Questions and releases evolve; compare results from compatible benchmark versions.',
    href: 'https://livebench.ai/',
    methodologyHref: 'https://github.com/LiveBench/LiveBench',
    modalities: ['language', 'coding'],
    launches: [
      {
        title: 'Test the same models on your prompt',
        model: 'Reasoning and fast routes',
        description: 'Use benchmark evidence to pick candidates, then measure the exact task, latency, and cost you will ship.',
        href: compare(['app/auto', 'app/auto-fast', 'gpt-5.4-mini', 'deepseek-v4-flash']),
        surface: 'Evals',
        tags: ['reasoning', 'fresh tasks'],
      },
      {
        title: 'Run a repeatable dataset eval',
        model: 'Any gateway models',
        description: 'Turn representative inputs into a scored regression set with an explicit judge rubric.',
        href: '/tools/bulk-evals',
        surface: 'Evals',
        tags: ['dataset', 'regression'],
      },
    ],
  },
  {
    slug: 'swe-bench',
    name: 'SWE-bench Verified',
    operator: 'SWE-bench',
    eyebrow: 'Repository-level software repair',
    description:
      'A human-validated 500-instance subset of real GitHub issues, evaluated by applying generated patches and running repository tests.',
    measures: [
      'Whether a generated patch resolves a real repository issue',
      'End-to-end coding systems on containerized repository tasks',
      'Language models under a shared minimal bash-agent scaffold',
    ],
    usefulFor: 'Shortlist coding models and agent systems for multi-file issue resolution rather than isolated code completion.',
    caution:
      'Agent scaffold, tools, retries, repository selection, and release version materially affect the result. Model-only and full-system tables answer different questions.',
    updateNote: 'Use the leaderboard agent filter and compare like-for-like harness versions.',
    href: 'https://www.swebench.com/verified.html',
    methodologyHref: 'https://www.swebench.com/SWE-bench/guides/evaluation/',
    modalities: ['coding'],
    launches: [
      {
        title: 'Run a cloud coding agent',
        model: 'Provider-aware coding agent',
        description: 'Give an app.nz agent a real repository issue and inspect its patch, tests, logs, and durable artifacts.',
        href: '/agent',
        surface: 'Agent',
        tags: ['repository', 'tests', 'patch'],
      },
      {
        title: 'Compare coding models first',
        model: 'Frontier coding panel',
        description: 'Use one issue and acceptance rubric to narrow the model panel before paying for full repository runs.',
        href: compare(['gpt-5.5', 'claude-sonnet-4-6', 'gemini-3.8-flash', 'deepseek-v4-flash']),
        surface: 'Evals',
        tags: ['coding', 'shortlist'],
      },
    ],
  },
  {
    slug: 'open-evals',
    name: 'Hugging Face OpenEvals',
    operator: 'Hugging Face community',
    eyebrow: 'Reproducible open-model evaluation',
    description:
      'Task-specific open leaderboards and evaluation artifacts, after the original Open LLM Leaderboard was retired.',
    measures: [
      'Task-specific open leaderboards rather than one permanent universal ranking',
      'Public model, dataset, harness, and result artifacts where a leaderboard provides them',
      'Open-weight models that can be downloaded and hosted independently',
    ],
    usefulFor: 'Find reproducible evidence for an open model, then follow it to the exact model repository or weights.',
    caution:
      '“Open” leaderboards vary in maintenance, task quality, licenses, quantization, prompts, and hardware. Inspect each leaderboard card and result artifact.',
    updateNote: 'The original Open LLM Leaderboard stopped accepting evaluations in 2025; use the maintained leaderboard directory.',
    href: 'https://huggingface.co/spaces/OpenEvals/find-a-leaderboard',
    methodologyHref: 'https://huggingface.co/docs/leaderboards/',
    modalities: ['language', 'coding', 'open-models'],
    launches: [
      {
        title: 'Link Hugging Face weights to Comfy',
        model: 'HF checkpoints, LoRAs, VAEs, ControlNets',
        description: 'Paste a public Hugging Face file URL, preserve provenance, and warm the shared R2 cache before a GPU starts.',
        href: '/comfy?tab=models&q=huggingface',
        surface: 'Comfy',
        tags: ['Hugging Face', 'R2 cache'],
      },
      {
        title: 'Browse hosted provider models',
        model: 'Open and proprietary routes',
        description: 'Check live gateway availability, pricing, context, and capabilities for the model you found.',
        href: '/models',
        surface: 'Gateway',
        tags: ['catalog', 'providers'],
      },
    ],
  },
];

export const evalSourceBySlug: Record<string, EvalSource> = Object.fromEntries(evalSources.map((source) => [source.slug, source]));

export const evalModalities: { id: EvalModality; label: string }[] = [
  { id: 'language', label: 'Language' },
  { id: 'coding', label: 'Coding' },
  { id: 'image', label: 'Image' },
  { id: 'video', label: 'Video' },
  { id: 'open-models', label: 'Open models' },
];
